import { apiClient, getErrorMessage } from '@/lib/apiClient';
import { ReadOnlyEntityService } from '@/lib/EntityService';
import { IdentityRole } from '@/types/identity/Identity';

/**
 * The Roles grid's service: /api/identity/role-list serves the list contract <EntityListView>
 * drives (column filters, sorting, paging) plus the grid's bulk delete. Read-only as far as the
 * shell's single-record CRUD goes — creating and editing a role stays on ABP's
 * /api/identity/roles through RoleDetailPage.
 */
class RoleListService extends ReadOnlyEntityService<IdentityRole> {
  constructor() {
    super('/api/identity/role-list', 'Role');
  }

  /** Delete the selected roles in one call (all-or-nothing on the server). */
  async deleteMany(ids: string[]): Promise<void> {
    try {
      await apiClient.post(`${this.baseUrl}/bulk-delete`, { ids });
    } catch (error) {
      console.error('Error bulk deleting roles:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Create a new role as a copy of the given one — same flags, same permission grants, never
   * static. The original error is rethrown so the dialog can extract the server's validation
   * messages (e.g. a name already in use).
   */
  async duplicate(sourceRoleId: string, name: string): Promise<IdentityRole> {
    const response = await apiClient.post<IdentityRole>(`${this.baseUrl}/duplicate`, { sourceRoleId, name });
    return response.data;
  }
}

export const roleListService = new RoleListService();
